'use client';

import cx from 'classix';
import { useAtomValue } from 'jotai/index';
import { useTranslations } from 'next-intl';

import { isMapViewAtom } from '@/features/listing/atoms/is-map-view-atom';
import ListingsSeoLinks from '@/features/listing/components/listings-section/listings-seo-links/listings-seo-links';
import {
  type ListingsCollection,
  type ListingType,
} from '@/features/listing/types/listing-types';
import { type SeoText } from '@/features/listing/types/seo-text-types';
import { type GeoPage } from '@/features/place/types/geo-page-types';
import { type Place } from '@/features/place/types/place-types';

import styles from './listings-seo.module.scss';

interface ListingsSeoProps {
  currentPlace: Place;
  listingType: ListingType;
  listingsCount: ListingsCollection['count'];
  averageMonthRent: ListingsCollection['averageMonthRent'],
  currentCurrencyName: string,
  geoPage: false | GeoPage;
  seoText: SeoText | null;
}

export default function ListingsSeo(props: ListingsSeoProps) {
  const {
    averageMonthRent,
    currentCurrencyName,
    currentPlace,
    geoPage,
    listingType,
    listingsCount,
    seoText,
  } = props;

  const translationsSeo = useTranslations('seo');

  const isMapView = useAtomValue(isMapViewAtom);

  return (
    <div className={cx(styles.listingsSeo, isMapView && styles.listingsSeo__isMapView)}>
      <div className={styles.listingsSeo_faq}>
        <h2 className={styles.listingsSeo_title}>{translationsSeo('faq.title')}</h2>
        <ul className={styles.listingsSeo_faqList}>
          <li className={styles.listingsSeo_faqItem}>
            <p className={styles.listingsSeo_faqQuestion}>
              {translationsSeo(
                `faq.items.listing-type-faq.${listingType}.available.question`,
                { location: currentPlace.fullName },
              )}
            </p>

            <p className={styles.listingsSeo_faqAnswer}>
              {translationsSeo(
                `faq.items.listing-type-faq.${listingType}.available.answer`,
                {
                  count: listingsCount,
                  location: currentPlace.fullName,
                },
              )}
            </p>
          </li>

          <li className={styles.listingsSeo_faqItem}>
            <p className={styles.listingsSeo_faqQuestion}>
              {translationsSeo(
                `faq.items.listing-type-faq.${listingType}.how-to-find.question`,
                { location: currentPlace.fullName },
              )}
            </p>

            <p className={styles.listingsSeo_faqAnswer}>
              {translationsSeo(
                `faq.items.listing-type-faq.${listingType}.how-to-find.answer`,
                { location: currentPlace.fullName },
              )}
            </p>
          </li>

          <li className={styles.listingsSeo_faqItem}>
            <p className={styles.listingsSeo_faqQuestion}>
              {translationsSeo(
                'faq.items.how-much-room.question',
                { location: currentPlace.fullName },
              )}
            </p>

            <p className={styles.listingsSeo_faqAnswer}>
              {translationsSeo(
                'faq.items.how-much-room.answer',
                {
                  amount: `${String(Math.ceil(averageMonthRent))} ${currentCurrencyName}`,
                  location: currentPlace.fullName,
                },
              )}
            </p>
          </li>
        </ul>
      </div>

      {seoText && (
        <div className={styles.listingsSeo_text}>
          <h2 className={styles.listingsSeo_textTitle}>
            {seoText.title}
          </h2>
          <br />
          <div dangerouslySetInnerHTML={{ __html: seoText.description }} />
        </div>
      )}

      {geoPage && geoPage.cityLinks && (
        <ListingsSeoLinks listingType={listingType} geoPage={geoPage} />
      )}
    </div>
  );
}
